import React, { ReactNode } from 'react';
import { CustomCellRendererProps, CustomTooltipProps } from '@ag-grid-community/react';
import { formatCurrency } from '../../components/tables/table-utils';
import { clsx } from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Channel,
  rateForTacticUnitPriceTypeId
} from 'shared/src/domain/line-item/line-item-channels';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { CalculatedValue } from '../../components/tables/cell-renderers/calculated-value';
import { RateCardDiff } from '../../components/tables/cell-renderers/rate-card-diff';
import { ViewButton } from '../../components/tables/cell-renderers/view-button';
import { TooltipContainer } from '../../components/tables/cell-renderers/utils';
import { isNil } from 'lodash';
import { LineItemApiResponse } from 'shared/src/api/line-item-api-types';
import {
  calcRateCardTargetUnitCost,
  formatPercentage
} from 'shared/src/domain/line-item/line-item-utils';
import { PacingIndicator } from '@/components/tables/cell-renderers/pacing-indicator';
import { BarChart } from '@/icons/bar-chart';

const CPM_ID = 1;

export type CellRendererProps<T> = CustomCellRendererProps<LineItemApiResponse, T>;
type TooltipProps = CustomTooltipProps<LineItemApiResponse>;
type RendererWithChannelsProps = { channels: Channel[] } & CellRendererProps<number>;

export function ViewButtonCellRenderer({ data }: CellRendererProps<string>) {
  if (!data || !data.id) return null;
  return <ViewButton path={`lineitems/${data.id}`} />;
}

export function PriceCellRenderer({ data, value }: CellRendererProps<number>) {
  if (!data || !value) return null;
  if (data.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  return <TextBox value={formatCurrency(value)} />;
}

export function PriceTooltip({ data, value }: TooltipProps) {
  if (!data || data.line_item_type === 'standard') return null;

  return data.line_item_type === 'management_fee' ? (
    <CenteredTooltip>
      Price = Management Fee of <span className={'font-bold'}>{formatCurrency(value)}</span>
    </CenteredTooltip>
  ) : (
    <CenteredTooltip>
      <div>
        Price is <span className="font-bold">$0.00</span>
      </div>
      <div>when the line item is Zero Dollar</div>
    </CenteredTooltip>
  );
}

export function PlannedMarginCellRenderer(props: RendererWithChannelsProps) {
  const { data, value } = props;
  if (data?.line_item_type === 'management_fee') return <MgmtFeeNotEditable value="N/A" />;
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="N/A" />;
  if (!data || !value) return null;

  return (
    <CalculatedValue
      disabled={false}
      value={plannedMarginTextValue(value)}
      diff={(100 * (value - data.target_margin)) / data.target_margin}
    />
  );
}

export function PlannedMarginTooltip({ data }: TooltipProps) {
  if (!data) return null;

  if (data.line_item_type === 'management_fee') {
    return (
      <CenteredTooltip>
        <div>
          Planned Margin is <span className="font-bold">N/A</span>
        </div>
        <div>when the line item is Management Fee</div>
        <div>since the Fee equals the Price</div>
      </CenteredTooltip>
    );
  } else if (data.line_item_type === 'zero_dollar') {
    return (
      <CenteredTooltip>
        <div>
          Planned Margin is <span className="font-bold">N/A</span>
        </div>
        <div>when the line item is Zero Dollar.</div>
        <div>However, the line item's cost will lower the Campaign Margin.</div>
      </CenteredTooltip>
    );
  } else {
    return (
      <CenteredTooltip>
        <div>Planned Margin is calculated as</div>
        <div className="font-bold">(Unit Price - Planned Unit Cost) / Unit Price</div>
        <div className="mt-1 text-sm text-gray-600">
          This value is automatically calculated based on your Planned Unit Cost
        </div>
      </CenteredTooltip>
    );
  }
}

function plannedMarginTextValue(value: number | null | undefined) {
  if (value == null) return 'Fills after Price Type';
  return `${(value * 100).toFixed(2)}%`;
}

export function UnitPriceCellRenderer(props: RendererWithChannelsProps) {
  const { data, value } = props;
  if (data?.line_item_type === 'management_fee') return <MgmtFeeNotEditable value="N/A" />;
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data) return null;
  return (
    <CopiedFrom
      disabled={false}
      value={unitPriceTextValue(data, value)}
      diff={determineUnitPriceDiff(props)}
    />
  );
}

export function UnitPriceTooltip({
  value,
  data,
  channels
}: TooltipProps & { channels: Channel[] }) {
  if (!data) return null;

  if (data.line_item_type === 'management_fee') {
    return (
      <CenteredTooltip>
        <div>Unit Price = Planned Unit Cost</div>
        <div>for Management Fee line items</div>
      </CenteredTooltip>
    );
  }

  if (data.line_item_type === 'zero_dollar') {
    return (
      <CenteredTooltip>
        <div>
          Unit Price is <span className="font-bold">$0.00</span>
        </div>
        <div>when the line item is Zero Dollar</div>
      </CenteredTooltip>
    );
  }

  const diff = determineUnitPriceDiff({ data, value, channels });
  const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
  if (!diff)
    return (
      <TooltipContainer>
        <div className="text-center">
          <div className="font-bold">{unitPriceTextValue(data, rate.unitPrice)}</div>
          <div>Rate Card Unit Price</div>
        </div>
      </TooltipContainer>
    );
  return (
    <TooltipContainer>
      <div className="text-center">
        <div>
          {Math.abs(diff).toFixed(1)}% {diff > 0 ? 'Above' : 'Below'} the
        </div>
        <div className="font-bold">{unitPriceTextValue(data, rate.unitPrice)}</div>
        <div>Rate Card Unit Price</div>
      </div>
    </TooltipContainer>
  );
}

type RateCardDiffParams = {
  data: LineItemApiResponse | undefined;
  value: number | null | undefined;
  channels: Channel[];
};

function unitPriceTextValue(
  data: LineItemApiResponse | undefined,
  value: number | null | undefined
) {
  if (value == null) return '';
  return formatCurrency(data?.unit_price_type_id === CPM_ID ? value * 1000 : value);
}

function determineUnitPriceDiff({ data, value, channels }: RateCardDiffParams) {
  if (data == null || value == null) return undefined;
  const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
  if (rate.unitPrice === 0 || value === rate.unitPrice) return undefined;
  return ((value - rate.unitPrice) / rate.unitPrice) * 100;
}

function determineUnitCostDiff({ data, value, channels }: RateCardDiffParams) {
  if (data == null || value == null) return undefined;
  const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
  const rateUnitCost = calcRateCardTargetUnitCost(rate);
  if (rateUnitCost === 0 || value === rateUnitCost) return undefined;
  return ((value - rateUnitCost) / rateUnitCost) * 100;
}

type RateCardValueProps = {
  value: string;
  diff?: number | undefined;
};

function RateCardValue({ value, diff }: RateCardValueProps) {
  return (
    <div className="h-full w-full p-1">
      <div className="flex h-full w-full items-center justify-between px-2">
        <div className="text-sm">{value}</div>
        {diff != null && <RateCardDiff change={diff} />}
      </div>
    </div>
  );
}

export function EstimatedUnitsRenderer({ data, value }: CellRendererProps<number>) {
  if (!data || !value) return null;
  return data.line_item_type === 'zero_dollar' ? (
    <TextBox value={value.toLocaleString()} />
  ) : (
    <CalculatedValue disabled={false} value={value.toLocaleString()} />
  );
}

export function EstimatedUnitsTooltip({ data }: TooltipProps) {
  if (!data) return null;
  const isCpm = data.unit_price_type_id === CPM_ID;

  if (data.line_item_type === 'management_fee') {
    const unitType = isCpm ? ' * 1,000 [CPM]' : '';
    const displayUnitCost = isCpm ? data.planned_unit_cost * 1000 : data.planned_unit_cost;
    return (
      <CenteredTooltip>
        <div>Estimated Units calculated as</div>
        <div>
          (<span className="font-bold">{formatCurrency(data.media_budget)}</span> Media Budget /{' '}
          <span className="font-bold">{formatCurrency(displayUnitCost)}</span> Planned Unit Cost)
          {unitType}
        </div>
        <div>for Management Fee line items</div>
      </CenteredTooltip>
    );
  }

  if (data.line_item_type === 'zero_dollar') {
    return (
      <CenteredTooltip>
        <div>Estimated Units are entered as</div>
        <div>the units to be delivered</div>
        <div>for Zero Dollar line items</div>
      </CenteredTooltip>
    );
  }

  const effectiveRate = data.referral_commission_rate ?? 0;

  if (effectiveRate > 0 && data.effective_unit_price) {
    // When there's a referral commission, use effective price and effective unit price

    const effectiveUnitPrice = data.effective_unit_price;

    const displayEffectivePrice = formatCurrency(data.effective_price);
    const displayEffectiveUnitPrice = formatCurrency(
      isCpm ? effectiveUnitPrice * 1000 : effectiveUnitPrice
    );
    const unitType = isCpm ? ' * 1,000 [CPM]' : '';

    return (
      <CenteredTooltip>
        <div>Estimated Units calculated as</div>
        <div>
          (<span className="font-bold">{displayEffectivePrice}</span> Effective Price /{' '}
          <span className="font-bold">{displayEffectiveUnitPrice}</span> Effective Unit Price)
          {unitType}
        </div>
      </CenteredTooltip>
    );
  } else {
    // Original calculation when no referral commission
    const displayPrice = formatCurrency(data.price);
    const unitPrice = data.unit_price || 0;
    const displayUnitPrice = formatCurrency(isCpm ? unitPrice * 1000 : unitPrice);
    const unitType = isCpm ? ' * 1,000 [CPM]' : '';

    return (
      <CenteredTooltip>
        <div>Estimated Units calculated as</div>
        <div>
          (<span className="font-bold">{displayPrice}</span> Price /{' '}
          <span className="font-bold">{displayUnitPrice}</span> Unit Price){unitType}
        </div>
      </CenteredTooltip>
    );
  }
}

export function PlannedUnitCostRenderer(
  props: CellRendererProps<number> & { channels: Channel[] }
) {
  const { data, value, channels } = props;
  if (!data || !value) return null;
  if (data.line_item_type === 'management_fee' || data.line_item_type === 'zero_dollar') {
    const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
    if (rate.unitPrice === 0)
      return <TextBox value={value || <div className="text-blue-500">Enter cost</div>} />;
  }

  return (
    <CopiedFrom
      value={formatCurrency(data.unit_price_type_id === CPM_ID ? value * 1000 : value)}
      diff={determineUnitCostDiff(props)}
    />
  );
}

export function EffectiveUnitPriceRenderer({ data, value }: CellRendererProps<number>) {
  if (data?.line_item_type === 'management_fee') return <MgmtFeeNotEditable value="N/A" />;
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data || !value) return null;

  const displayValue = data?.unit_price_type_id === CPM_ID ? value * 1000 : value;

  return <CalculatedValue disabled={false} value={formatCurrency(displayValue)} />;
}

export function EffectiveUnitPriceTooltip({ data }: TooltipProps) {
  if (!data) return null;

  if (data.line_item_type === 'management_fee') {
    return (
      <CenteredTooltip>
        <div>
          Effective Unit Price is <span className="font-bold">N/A</span>
        </div>
        <div>when the line item is Management Fee</div>
      </CenteredTooltip>
    );
  }

  if (data.line_item_type === 'zero_dollar') {
    return (
      <CenteredTooltip>
        <div>
          Effective Unit Price is <span className="font-bold">$0.00</span>
        </div>
        <div>when the line item is Zero Dollar</div>
      </CenteredTooltip>
    );
  }

  // Standard line items must have a unit price
  if (data.unit_price == null) return;
  const unitPrice = data.unit_price_type_id === CPM_ID ? data.unit_price * 1000 : data.unit_price;
  const formula = `${formatCurrency(unitPrice)} Unit Price × (1 - ${(data.referral_commission_rate * 100).toFixed(1)}% Rate)`;

  return (
    <CenteredTooltip>
      <div>Effective Unit Price calculated as</div>
      <div className="font-bold">{formula}</div>
    </CenteredTooltip>
  );
}

export function PlannedUnitCostTooltip({
  data,
  value,
  channels
}: TooltipProps & { channels: Channel[] }) {
  if (!data) return null;

  // For management_fee and zero_dollar, show comparison to rate card
  if (data.line_item_type === 'management_fee' || data.line_item_type === 'zero_dollar') {
    const diff = determineUnitCostDiff({ data, value, channels });
    const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
    const rateUnitCost = calcRateCardTargetUnitCost(rate);
    if (!diff)
      return (
        <CenteredTooltip>
          <div className="font-bold">{unitPriceTextValue(data, rateUnitCost)}</div>
          <div>Rate Card Unit Cost</div>
        </CenteredTooltip>
      );
    return (
      <CenteredTooltip>
        <div>
          {Math.abs(diff).toFixed(1)}% {diff > 0 ? 'Above' : 'Below'} the
        </div>
        <div className="font-bold">{unitPriceTextValue(data, rateUnitCost)}</div>
        <div>Rate Card Unit Cost</div>
      </CenteredTooltip>
    );
  }

  const diff = determineUnitCostDiff({ data, value, channels });
  const rate = rateForTacticUnitPriceTypeId(channels, data.tactic_id, data.unit_price_type_id);
  const rateUnitCost = calcRateCardTargetUnitCost(rate);

  if (!diff) {
    return (
      <CenteredTooltip>
        <div>Enter your Planned Unit Cost</div>
        <div className="mt-1 text-sm text-gray-600">
          Rate Card suggests:{' '}
          <span className="font-bold">{unitPriceTextValue(data, rateUnitCost)}</span>
        </div>
      </CenteredTooltip>
    );
  }

  return (
    <CenteredTooltip>
      <div>Your Planned Unit Cost is</div>
      <div>
        {Math.abs(diff).toFixed(1)}% {diff > 0 ? 'Above' : 'Below'} the
      </div>
      <div className="font-bold">{unitPriceTextValue(data, rateUnitCost)}</div>
      <div>Rate Card Suggested Cost</div>
    </CenteredTooltip>
  );
}

export function MediaBudgetRenderer({ data, value }: CellRendererProps<number>) {
  if (!data || !value) return null;

  return data.line_item_type === 'management_fee' ? (
    <TextBox value={formatCurrency(Number(value))} />
  ) : (
    <CalculatedValue disabled={false} value={formatCurrency(value)} />
  );
}

export function MediaBudgetTooltip({ data }: TooltipProps) {
  if (!data) return null;
  const { referral_commission_rate, line_item_type, estimated_units } = data;

  if (line_item_type === 'management_fee') {
    return (
      <CenteredTooltip>
        <div>
          <div className="font-bold">Media Budget</div>
          is the amount to be spent
        </div>
        <div>in the advertiser's media account</div>
        <div>for Management Fee line items</div>
      </CenteredTooltip>
    );
  }

  if (line_item_type === 'zero_dollar') {
    const isCpm = data.unit_price_type_id === CPM_ID;
    const unitType = isCpm ? '/1,000 for CPM' : '';
    const displayUnitCost = isCpm ? data.planned_unit_cost * 1000 : data.planned_unit_cost;

    return (
      <CenteredTooltip>
        <div>Media Budget calculated as</div>
        <div>
          {unitType ? (
            <span>
              (<span className="font-bold">{estimated_units.toLocaleString()}</span> {unitType})
            </span>
          ) : (
            estimated_units.toLocaleString()
          )}
          Estimated Units * <span className="font-bold">{formatCurrency(displayUnitCost)}</span>{' '}
          Planned Unit Cost
        </div>
        <div>for Zero Dollar line items</div>
      </CenteredTooltip>
    );
  }

  const effectiveRate = referral_commission_rate ?? 0;

  const formula = `${formatCurrency(data.effective_price)} ${effectiveRate > 0 ? 'Effective Price' : 'Price'} × (1 - ${(data.planned_margin * 100).toFixed(1) + '%'} Planned Margin)`;

  return (
    <CenteredTooltip>
      <div>Media Budget calculated as</div>
      <div className="font-bold">{formula}</div>
    </CenteredTooltip>
  );
}

export function ReferralFeeRenderer({ data, value }: CellRendererProps<number>) {
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data || !value) return null;
  else return <CalculatedValue disabled={false} value={formatCurrency(value)} />;
}

export function ReferralFeeTooltip({ data }: TooltipProps) {
  if (!data) return null;

  if (data.line_item_type === 'zero_dollar') {
    return (
      <TooltipContainer>
        <div className="text-center">
          <div>
            Referral Fee is <span className="font-bold">$0.00</span>
          </div>
          <div>when the line item is Zero Dollar</div>
        </div>
      </TooltipContainer>
    );
  }

  if (data.price == null) return null;

  const formula = `${formatCurrency(data.price)} Price × ${((data.referral_commission_rate ?? 0) * 100).toFixed(1)}% Rate`;

  return (
    <CenteredTooltip>
      <div>Referral Fee calculated as</div>
      <div className="font-bold">{formula}</div>
    </CenteredTooltip>
  );
}

export function EffectivePriceRenderer({ data, value }: CellRendererProps<number>) {
  if (!data || !value) return null;
  return data.line_item_type === 'zero_dollar' ? (
    <ZeroDollarNotEditable value="$0.00" />
  ) : (
    <CalculatedValue disabled={data?.price == null} value={formatCurrency(value)} />
  );
}

export function EffectivePriceTooltip({ data }: TooltipProps) {
  if (!data) return null;

  if (data.line_item_type === 'zero_dollar') {
    return (
      <TooltipContainer>
        <div className="text-center">
          <div>
            Effective Price is <span className="font-bold">$0.00</span>
          </div>
          <div>when the line item is Zero Dollar</div>
        </div>
      </TooltipContainer>
    );
  }

  if (data.price == null) return null;

  const displayPrice = formatCurrency(data.price);
  const formula = `${displayPrice} Price × (1 - ${((data.referral_commission_rate ?? 0) * 100).toFixed(1)}% Rate)`;

  return (
    <CenteredTooltip>
      <div>Effective Price calculated as</div>
      <div className="font-bold">{formula}</div>
    </CenteredTooltip>
  );
}

// Agency Markup Cell Renderers
export function AgencyMarkupRenderer({ data, value }: CellRendererProps<number>) {
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data || !value) return null;
  return <CalculatedValue disabled={false} value={formatCurrency(data.gross_price - data.price)} />;
}

export function GrossPriceRenderer({ data, value }: CellRendererProps<number>) {
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data || !value) return null;

  return <CalculatedValue disabled={false} value={formatCurrency(value)} />;
}

export function GrossUnitPriceRenderer({ data, value }: CellRendererProps<number>) {
  if (data?.line_item_type === 'management_fee') return <MgmtFeeNotEditable value="N/A" />;
  if (data?.line_item_type === 'zero_dollar') return <ZeroDollarNotEditable value="$0.00" />;
  if (!data || !value) return null;

  return <CalculatedValue disabled={false} value={unitPriceTextValue(data, value)} />;
}

export function AgencyMarkupTooltip({ data }: TooltipProps) {
  if (!data || data.price == null) return null;

  const displayPrice = formatCurrency(data.price);
  const grossPrice = data.gross_price;
  const formula = `${grossPrice} Gross Price - ${displayPrice} Price`;

  return (
    <CenteredTooltip>
      <div>Agency Fee calculated as</div>
      <div className="font-bold">{formula}</div>
      <div className="mt-1 text-xs">
        Using {(data.agency_markup_rate * 100).toFixed(1)}% Markup Rate
      </div>
    </CenteredTooltip>
  );
}

export function GrossUnitPriceTooltip({ data }: TooltipProps) {
  if (!data) return null;

  if (data.line_item_type === 'zero_dollar') {
    return (
      <CenteredTooltip>
        <div>
          Gross Unit Price is <span className="font-bold">$0.00</span>
        </div>
        <div>when the line item is Zero Dollar</div>
      </CenteredTooltip>
    );
  }

  if (data.line_item_type === 'management_fee') {
    return (
      <CenteredTooltip>
        <div>
          Gross Unit Price is <span className="font-bold">N/A</span>
        </div>
        <div>when the line item is Management Fee</div>
      </CenteredTooltip>
    );
  }

  if (data.unit_price == null || data.agency_markup_rate === 0) return null;

  const displayRate = data.agency_markup_rate ? formatPercentage(data.agency_markup_rate) : '0%';

  return (
    <TooltipContainer>
      <div className="text-center">
        <div>Gross Unit Price calculated as</div>
        <div>
          <span className="font-bold">{unitPriceTextValue(data, data.unit_price)}</span> Unit Price
          / (1 + <span className="font-bold">{displayRate}</span> Agency Markup)
        </div>
      </div>
    </TooltipContainer>
  );
}

export function GrossPriceTooltip({
  data,
  agencyMarkupRate
}: TooltipProps & { agencyMarkupRate?: number | null }) {
  if (!data) return null;

  if (data.line_item_type === 'zero_dollar') {
    return (
      <TooltipContainer>
        <div className="text-center">
          <div>
            Gross Price is <span className="font-bold">$0.00</span>
          </div>
          <div>when the line item is Zero Dollar</div>
        </div>
      </TooltipContainer>
    );
  }

  if (data.price == null) return null;

  const effectiveRate = data.agency_markup_rate ?? agencyMarkupRate ?? 0;
  if (effectiveRate === 0) return null;

  const displayPrice = formatCurrency(data.price);
  const displayRate = agencyMarkupRate ? formatPercentage(agencyMarkupRate) : '0%';

  return (
    <TooltipContainer>
      <div className="text-center">
        <div>Gross Price calculated as</div>
        <div>
          <span className="font-bold">{displayPrice}</span> Price / (1 +{' '}
          <span className="font-bold">{displayRate}</span> Agency Markup)
        </div>
      </div>
    </TooltipContainer>
  );
}

export function NullPacingDetails() {
  return <div>-</div>;
}

export function EmptyPacingDetails() {
  return (
    <div className="flex cursor-pointer items-center text-red-500">
      <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
      <div>Incomplete</div>
    </div>
  );
}

function ZeroDollarNotEditable({ value }: { value: string }) {
  return (
    <div className="flex h-full w-full items-center p-1">
      <div className="flex h-full w-full items-center justify-between text-ellipsis rounded-sm bg-gray-100 px-2 py-px">
        <div className="flex h-full items-center pr-4 text-gray-500">Zero Dollar Line Item</div>
        <div className="flex h-full items-center border-l border-gray-300 pl-2">{value}</div>
      </div>
    </div>
  );
}

function MgmtFeeNotEditable({ value }: { value: string }) {
  return (
    <div className="flex h-full w-full items-center p-1">
      <div className="flex h-full w-full items-center justify-between text-ellipsis rounded-sm bg-gray-100 px-2 py-px">
        <div className="flex h-full items-center pr-4 text-gray-500">MGMT Fee Line Item</div>
        <div className="flex h-full items-center border-l border-gray-300 pl-2">{value}</div>
      </div>
    </div>
  );
}

function TextBox({ value, suffix }: { value: ReactNode; suffix?: string }) {
  return (
    <div className="flex h-full w-full items-center p-1">
      <div className="flex h-full w-full items-center justify-end overflow-clip rounded-sm border border-blue-200 px-2 py-1">
        <div className={clsx('flex h-full items-center', suffix && 'pr-4')}>{value}</div>
        {suffix && (
          <div className="flex h-full items-center border-l border-gray-300 pl-2 capitalize text-gray-500">
            {suffix}
          </div>
        )}
      </div>
    </div>
  );
}

function CopiedFrom({
  disabled,
  value,
  diff
}: {
  disabled?: boolean;
  value: string;
  diff?: number;
}) {
  return (
    <div className={clsx('flex h-full w-full items-center p-1', disabled && 'opacity-35')}>
      <div className="flex h-full w-full items-center justify-between px-2">
        {diff != null && <RateCardDiff change={diff} />}
        <div className="ml-auto text-sm">{value}</div>
      </div>
    </div>
  );
}

export function TargetMarginCellRenderer({ data, value }: CellRendererProps<number>) {
  if (!data || !value) return null;
  return <RateCardValue value={formatPercentage(value)} />;
}

export function TargetUnitPriceCellRenderer(props: CellRendererProps<number>) {
  const { data, value } = props;
  if (!data) return null;
  const { line_item_type, unit_price_type_id } = data;
  if (line_item_type === 'standard' && !isNil(value)) {
    return (
      <RateCardValue value={formatCurrency(unit_price_type_id === CPM_ID ? value * 1000 : value)} />
    );
  }

  return <RateCardValue value="$0.00" />;
}

export function TargetUnitCostCellRenderer(props: CellRendererProps<number>) {
  const { data, value } = props;
  if (!data || !value) return null;
  const isCpm = data.unit_price_type_id === CPM_ID;
  return <RateCardValue value={formatCurrency(isCpm ? value * 1000 : value)} />;
}

export function TargetMarginTooltip({ data }: TooltipProps) {
  if (!data) return null;

  return (
    <CenteredTooltip>
      <div>The target profit margin for this tactic</div>
      <div>and unit price type from the rate card.</div>
    </CenteredTooltip>
  );
}

export function TargetUnitPriceTooltip({ data }: TooltipProps) {
  if (!data) return null;

  return (
    <CenteredTooltip>
      <div>The target unit price for this tactic</div>
      <div>and unit price type from the rate card.</div>
    </CenteredTooltip>
  );
}

export function TargetUnitCostTooltip({ data }: TooltipProps) {
  if (!data) return null;

  return (
    <CenteredTooltip>
      <div>The target unit cost for this tactic</div>
      <div>and unit price type from the rate card.</div>
    </CenteredTooltip>
  );
}

export function DeliveryPacingRenderer({ value }: CellRendererProps<number>) {
  return <PacingIndicator value={value} icon={<BarChart />} />;
}

export function SpendPacingRenderer({ value }: CellRendererProps<number>) {
  return <PacingIndicator value={value} icon={<BarChart />} />;
}

export function MarginRenderer({ data, value }: CellRendererProps<number>) {
  return <PacingIndicator value={value} colorBase={data?.planned_margin} icon={<BarChart />} />;
}

function CenteredTooltip({ children }: { children: ReactNode }) {
  return (
    <TooltipContainer>
      <div className="text-center">{children}</div>
    </TooltipContainer>
  );
}

// Safely determine what the correct column value is to render
export function getValueString(value: unknown): string | number | undefined {
  if (value != null) {
    if (typeof value === 'object' && 'name' in value && typeof value.name === 'string') {
      return value.name;
    }
    if (Array.isArray(value)) {
      return value
        .map(v => getValueString(v))
        .filter(v => v != null)
        .join(', ');
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    return undefined;
  }
  return '';
}
